import { cubicAt } from './geometry.js';
import type { Vec } from './types.js';

/**
 * A tiny path IR shared by the canvas renderer, the SVG exporter and the
 * hit-tester. Arcs are deliberately absent: every curve is a cubic bezier, so
 * one flattening routine serves all three consumers and exported SVG matches
 * the canvas pixel for pixel.
 */
export type PathCmd =
  | { c: 'M'; x: number; y: number }
  | { c: 'L'; x: number; y: number }
  | { c: 'Q'; cx: number; cy: number; x: number; y: number }
  | { c: 'C'; c1x: number; c1y: number; c2x: number; c2y: number; x: number; y: number }
  | { c: 'Z' };

export type Path = PathCmd[];

export const M = (x: number, y: number): PathCmd => ({ c: 'M', x, y });
export const L = (x: number, y: number): PathCmd => ({ c: 'L', x, y });
export const Q = (cx: number, cy: number, x: number, y: number): PathCmd => ({
  c: 'Q',
  cx,
  cy,
  x,
  y,
});
export const C = (
  c1x: number,
  c1y: number,
  c2x: number,
  c2y: number,
  x: number,
  y: number,
): PathCmd => ({ c: 'C', c1x, c1y, c2x, c2y, x, y });
export const Z: PathCmd = { c: 'Z' };

/** Closed polygon helper. */
export function poly(points: readonly Vec[]): Path {
  if (points.length === 0) return [];
  const out: Path = [M(points[0]!.x, points[0]!.y)];
  for (let i = 1; i < points.length; i++) out.push(L(points[i]!.x, points[i]!.y));
  out.push(Z);
  return out;
}

export function pathToSvg(path: Path, precision = 2): string {
  const n = (v: number) => Number(v.toFixed(precision)).toString();
  const parts: string[] = [];
  for (const cmd of path) {
    switch (cmd.c) {
      case 'M':
        parts.push(`M${n(cmd.x)} ${n(cmd.y)}`);
        break;
      case 'L':
        parts.push(`L${n(cmd.x)} ${n(cmd.y)}`);
        break;
      case 'Q':
        parts.push(`Q${n(cmd.cx)} ${n(cmd.cy)} ${n(cmd.x)} ${n(cmd.y)}`);
        break;
      case 'C':
        parts.push(
          `C${n(cmd.c1x)} ${n(cmd.c1y)} ${n(cmd.c2x)} ${n(cmd.c2y)} ${n(cmd.x)} ${n(cmd.y)}`,
        );
        break;
      case 'Z':
        parts.push('Z');
        break;
    }
  }
  return parts.join(' ');
}

/** Applies a path to any Canvas2D-like sink (CanvasRenderingContext2D, Path2D). */
export interface PathSink {
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cx: number, cy: number, x: number, y: number): void;
  bezierCurveTo(
    c1x: number,
    c1y: number,
    c2x: number,
    c2y: number,
    x: number,
    y: number,
  ): void;
  closePath(): void;
}

export function applyPath(path: Path, sink: PathSink): void {
  for (const cmd of path) {
    switch (cmd.c) {
      case 'M':
        sink.moveTo(cmd.x, cmd.y);
        break;
      case 'L':
        sink.lineTo(cmd.x, cmd.y);
        break;
      case 'Q':
        sink.quadraticCurveTo(cmd.cx, cmd.cy, cmd.x, cmd.y);
        break;
      case 'C':
        sink.bezierCurveTo(cmd.c1x, cmd.c1y, cmd.c2x, cmd.c2y, cmd.x, cmd.y);
        break;
      case 'Z':
        sink.closePath();
        break;
    }
  }
}

/**
 * Flattens into one polygon per subpath. `steps` controls curve resolution;
 * hit-testing is happy with 12, export previews want more.
 */
export function flattenPath(path: Path, steps = 12): Vec[][] {
  const out: Vec[][] = [];
  let current: Vec[] = [];
  let cursor: Vec = { x: 0, y: 0 };
  let subpathStart: Vec = { x: 0, y: 0 };

  const push = (p: Vec) => {
    current.push(p);
    cursor = p;
  };

  for (const cmd of path) {
    switch (cmd.c) {
      case 'M':
        if (current.length > 1) out.push(current);
        current = [];
        subpathStart = { x: cmd.x, y: cmd.y };
        push(subpathStart);
        break;
      case 'L':
        push({ x: cmd.x, y: cmd.y });
        break;
      case 'Q': {
        // Elevate the quadratic to a cubic so one sampler covers both.
        const c1 = { x: cursor.x + (2 / 3) * (cmd.cx - cursor.x), y: cursor.y + (2 / 3) * (cmd.cy - cursor.y) };
        const c2 = { x: cmd.x + (2 / 3) * (cmd.cx - cmd.x), y: cmd.y + (2 / 3) * (cmd.cy - cmd.y) };
        const from = cursor;
        for (let i = 1; i <= steps; i++) {
          current.push(cubicAt(from, c1, c2, { x: cmd.x, y: cmd.y }, i / steps));
        }
        cursor = { x: cmd.x, y: cmd.y };
        break;
      }
      case 'C': {
        const from = cursor;
        const c1 = { x: cmd.c1x, y: cmd.c1y };
        const c2 = { x: cmd.c2x, y: cmd.c2y };
        const to = { x: cmd.x, y: cmd.y };
        for (let i = 1; i <= steps; i++) current.push(cubicAt(from, c1, c2, to, i / steps));
        cursor = to;
        break;
      }
      case 'Z':
        if (current.length > 0) {
          current.push({ x: subpathStart.x, y: subpathStart.y });
          out.push(current);
          current = [];
          cursor = subpathStart;
        }
        break;
    }
  }
  if (current.length > 1) out.push(current);
  return out;
}

export function translatePath(path: Path, dx: number, dy: number): Path {
  return path.map((cmd) => {
    switch (cmd.c) {
      case 'M':
        return M(cmd.x + dx, cmd.y + dy);
      case 'L':
        return L(cmd.x + dx, cmd.y + dy);
      case 'Q':
        return Q(cmd.cx + dx, cmd.cy + dy, cmd.x + dx, cmd.y + dy);
      case 'C':
        return C(cmd.c1x + dx, cmd.c1y + dy, cmd.c2x + dx, cmd.c2y + dy, cmd.x + dx, cmd.y + dy);
      case 'Z':
        return Z;
    }
  });
}
